/* eslint-disable react-hooks/exhaustive-deps */
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import {
  Card,
  Table,
  Button,
  Tooltip,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  LinearProgress,
} from '@mui/material';
// routes
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { PERMISSIONS } from '../utils/constant';
import ProfilesTableRow from '../sections/@dashboard/profiles/ProfilesTableRow';
import { deleteProfile, fetchProfiles, updateDebouncedSearch } from '../store/slices/profilesSlice';
import ProfilesTableToolbar from '../sections/@dashboard/profiles/ProfilesTableToolbar';
import { PATH_DASHBOARD } from '../routes/paths';
// _mock_
// components
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import ConfirmDialog from '../components/confirm-dialog';
import CustomBreadcrumbs from '../components/custom-breadcrumbs';
import { useSettingsContext } from '../components/settings';
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from '../components/table';

// sections

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

export default function Profiles() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch } = useSettingsContext();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { tableData, loading, pagination, debouncedSearch, permissions } = useSelector((state) => ({
    tableData: state.profiles.data,
    loading: state.profiles.loading,
    debouncedSearch: state.profiles.debouncedSearch,
    pagination: state.profiles.pagination,
    permissions: state.auth.permissions,
  }));

  const TABLE_HEAD = [
    { id: 'profile_code', label: 'Code', sortable: true },
    { id: 'users', label: 'Users Count' },
    { id: 'description', label: 'Description' },
    { id: 'default_hours', label: 'Default Hours', align: 'center', sortable: true },
    {
      id: '',
      label: 'Actions',
      align: 'right',
      hidden: !(permissions[PERMISSIONS.PROFILE_UPDATE] || permissions[PERMISSIONS.PROFILE_DELETE]),
    },
  ];
  const navigate = useNavigate();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [debounceSearch, setDeboucedSearch] = useState(null);

  const dataFiltered = applyFilter({
    inputData: tableData || [],
    comparator: getComparator(order, orderBy),
  });

  const dataInPage = dataFiltered?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const denseHeight = dense ? 52 : 72;

  const isFiltered = filterName !== '' || filterRole !== 'all' || filterStatus !== 'all';

  const isNotFound = tableData?.length <= 0;

  const handleOpenConfirm = () => setOpenConfirm(true);

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleFilterName = (event) => {
    setFilterName(event.target.value);
    if (!debouncedSearch) {
      dispatch(
        updateDebouncedSearch(
          debounce(() => {
            if (event) {
              setPage(0);
              setDeboucedSearch(event.target.value);
            }
          }, 500)
        )
      );
    }
    // eslint-disable-next-line no-unused-expressions
    debouncedSearch && debouncedSearch();
  };

  const handleFilterRole = (event) => {
    setPage(0);
    setFilterRole(event.target.value);
  };

  const handleDeleteRow = (id) => {
    dispatch(deleteProfile(id))
      .unwrap()
      .then((res) => {
        enqueueSnackbar(res.message);
        dispatch(fetchProfiles());
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }));

    if (page > 0) {
      if (dataInPage?.length < 2) {
        setPage(page - 1);
      }
    }
  };

  const handleEditRow = (id) => navigate(PATH_DASHBOARD.profiles.edit(id));

  const handleResetFilter = () => {
    setFilterName('');
    setDeboucedSearch(' ');
    setFilterRole('all');
    setFilterStatus('all');
  };

  useEffect(() => {
    dispatch(
      fetchProfiles({
        page,
        rowsPerPage,
        search: debounceSearch,
        status: filterStatus,
        order,
        orderBy,
      })
    );
  }, [debounceSearch, filterStatus, page, rowsPerPage, order, orderBy]);

  return (
    <>
      <Helmet>
        <title> {` Profiles | ${process.env.REACT_APP_NAME} `}</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : 'xl'}>
        <CustomBreadcrumbs
          heading="Profiles"
          links={[
            { name: 'Management' },
            { name: 'Manage Profiles', href: PATH_DASHBOARD.profiles },
          ]}
          action={
            permissions[PERMISSIONS.PROFILE_ADD] && (
              <Button
                component={RouterLink}
                to={PATH_DASHBOARD.profiles.new}
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Add New Profile
              </Button>
            )
          }
        />

        <Card sx={{ mx: { xs: '-10px', sm: 0 } }}>
          <ProfilesTableToolbar
            isFiltered={isFiltered}
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
            onResetFilter={handleResetFilter}
          />

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={dense}
              numSelected={selected?.length}
              rowCount={pagination?.total_records}
              onSelectAllRows={(checked) =>
                onSelectAllRows(
                  checked,
                  tableData?.map((row) => row.id)
                )
              }
              action={
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={handleOpenConfirm}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              {loading ? (
                <Box sx={{ width: 1 }}>
                  <LinearProgress />
                </Box>
              ) : (
                <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                  <>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={pagination?.total_records}
                      numSelected={selected?.length}
                      onSort={onSort}
                    />
                    <TableBody>
                      {tableData?.map((row) => (
                        <ProfilesTableRow
                          key={row.id}
                          row={row}
                          selected={selected.includes(row.id)}
                          onSelectRow={() => onSelectRow(row.id)}
                          onDeleteRow={() => handleDeleteRow(row.id)}
                          onEditRow={() => handleEditRow(row.id)}
                        />
                      ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(page, rowsPerPage, tableData?.length)}
                      />

                      <TableNoData isNotFound={isNotFound} />
                    </TableBody>
                  </>
                </Table>
              )}
            </Scrollbar>
          </TableContainer>
          <TablePaginationCustom
            count={pagination?.total_records}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            dense={dense}
            onChangeDense={onChangeDense}
          />
        </Card>
      </Container>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Delete"
        content={
          <>
            Are you sure want to delete <strong> {selected?.length} </strong> items?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleCloseConfirm();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}

// ----------------------------------------------------------------------

export const applyFilter = ({ inputData, comparator, filterName, filterStatus, filterRole }) => {
  const stabilizedThis = inputData?.map((el, index) => [el, index]);

  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis?.map((el) => el[0]);

  if (filterName) {
    inputData = inputData?.filter(
      (user) => user.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== 'all') {
    inputData = inputData?.filter((user) => user.status === filterStatus);
  }

  if (filterRole !== 'all') {
    inputData = inputData?.filter((user) => user.role === filterRole);
  }

  return inputData;
};
